<template>
  <Card
    :title="t('ui.billingPayment.details.title')"
    :status="cardStatusColor"
    :statusText="cardStatusText"
    :disabled="isCardDisabled"
    class="BillingPaymentDetailsCard"
    :class="{
      'is-disabled': isCardDisabled,
    }"
  >
    <template #header-action>
      <DropdownMenu
        v-if="!isCardDisabled"
        class="BillingPaymentDetailsCard-actions"
        :title="t('ui.billingPayment.details.dropdown.label')"
        :items="dropdownItems"
      >
        <template #trigger>
          <ActionButton
            v-if="isTabletOrDesktop"
            :disabled="isCardDisabled"
            class="BillingPaymentDetailsCard-actionsTrigger"
            variant="tertiary"
            rightIcon="dots-vertical"
          >
            {{ t('ui.subscription.details.dropdown.label') }}
          </ActionButton>

          <IconButton
            v-else
            class="BillingPaymentDetailsCard-actionsTrigger"
            icon="dots-vertical"
            size="md"
            :ariaLabel="t('ui.subscription.details.dropdown.label')"
          />
        </template>
      </DropdownMenu>
    </template>

    <div class="BillingPaymentDetailsCard-row">
      <div class="BillingPaymentDetailsCard-column">
        <BillingPaymentContent
          v-if="billingPaymentDetails"
          v-bind="billingPaymentDetails"
          :showPriceTooltip="showPriceTooltip"
        />
      </div>

      <div class="BillingPaymentDetailsCard-column BillingPaymentDetailsCard-column--card">
        <CreditCardDisplay
          v-if="billingPaymentDetails.cardDetails"
          :title="t('ui.billingPayment.details.card.title')"
          :brand="billingPaymentDetails.cardDetails.brand"
          :creditCardBrandIconUrl="billingPaymentDetails.creditCardBrandIconUrl"
          :last4="billingPaymentDetails.cardDetails.last4"
          :expiryMonth="billingPaymentDetails.cardDetails.expiryMonth"
          :expiryYear="billingPaymentDetails.cardDetails.expiryYear"
          :cardholderName="billingPaymentDetails.cardDetails.cardholderName"
        />
      </div>
    </div>

    <p
      v-if="billingPaymentDetails.promo.isPromotional && billingPaymentDetails.promo.revertsAt"
      class="BillingPaymentContent-disclaimer"
    >
      {{ t('ui.billingPayment.details.promo.disclaimer', {validUntil: billingPaymentDetails.promo.revertsAt, price: billingPaymentDetails.promo.fullPrice, planFrequency: t(`custom.plans.recurrency.${billingPaymentDetails.planFrequency}.short`)}) }}
    </p>

    <p
      v-if="disclaimerText"
      class="BillingPaymentContent-disclaimer"
    >
      {{ disclaimerText }}
    </p>

    <slot />
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useResponsive, useSubscriptionStatus } from '@cvo/ui/composables';
import Card from '../../atoms/Card/Card.vue';
import DropdownMenu from '../../molecules/DropdownMenu/DropdownMenu.vue';
import type { DropdownMenuItem } from '../../molecules/DropdownMenu/DropdownMenu.model';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import CreditCardDisplay from '../CreditCardDisplay/CreditCardDisplay.vue';
import type { BillingPaymentDetailsProps } from './BaseBillingPaymentDetailsCard.model';
import BillingPaymentContent from './BillingPaymentContent.vue';

const props = withDefaults(defineProps<BillingPaymentDetailsProps>(), {
  showPriceTooltip: true,
});

const emit = defineEmits<{
  (e: 'change-card'): void;
}>();

const { t } = useI18n();
const { isTabletOrDesktop } = useResponsive();

const { cardStatusColor, cardStatusText, isCardDisabled } = useSubscriptionStatus(() => props.subscription);

const dropdownItems = computed<DropdownMenuItem[]>(() => [
  {
    id: 'change-card',
    icon: 'cached',
    label: t('ui.billingPayment.details.dropdown.changeCard'),
    action: () => emit('change-card'),
  },
]);

function getDisclaimerTranslationKey(hasSignUpFee: boolean, hasRenewalFee: boolean): string | null {
  if (hasSignUpFee && hasRenewalFee) return 'ui.billingPayment.details.subscriptionFees.signUpAndRenewalFees';
  if (hasSignUpFee) return 'ui.billingPayment.details.subscriptionFees.signUpFeeOnly';
  if (hasRenewalFee) return 'ui.billingPayment.details.subscriptionFees.renewalFeeOnly';
  return null;
}

function getDisclaimerText(details: BillingPaymentDetailsProps['billingPaymentDetails']): string {
  if (!details?.renewalFeeFrequency) return '';

  const { signUpFee, renewalFee, renewalFeeFrequency, planFrequency } = details;
  const key = getDisclaimerTranslationKey(!!signUpFee, !!renewalFee);
  if (!key) return '';

  return t(key, {
    signUpFee,
    renewalFeeFrequency,
    renewalFee,
    signUpFeePrice: signUpFee,
    renewalFeePrice: renewalFee,
    planFrequency: t(`custom.plans.recurrency.${planFrequency}.long`).toLocaleLowerCase(),
  });
}

const disclaimerText = computed(() => getDisclaimerText(props.billingPaymentDetails));
</script>

<style lang="scss">
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .BillingPaymentDetailsCard {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-6;
    padding: vars.$dimensions-d-5;
    position: relative;

    &-actionsTrigger {
      padding-inline: 0;
    }

    &.is-disabled {
      .BillingPaymentContent-price,
      .BillingPaymentContent-listItem,
      .BillingPaymentContent-disclaimer,
      .CreditCardDisplay {
        opacity: 0.5;
        filter: grayscale(1);
      }
    }

    &-row {
      display: flex;
      gap: vars.$dimensions-d-5;

      @include responsive.tablet-down {
        flex-direction: column;
      }
    }

    &-column {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;

      &--card {
        @include responsive.desktop {
          max-width: 300px;
        }
      }
    }
  }
}
</style>
